import { useEffect, useMemo, useRef, useState } from "react";
import {
  meetingAPI, attendeeAPI, meetingMinuteAPI,
  meetingRoomAPI, lookupAPI, userAPI,
} from "../services/api";
import { Modal, FormField, Btn, ConfirmDialog, Toast, Can, errMessage, TableScroll, Pagination } from "../components/shared/index";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { canDo } from "../utils/permissions";
import {
  MdSearch, MdCalendarToday, MdMeetingRoom, MdEdit, MdLock,
  MdDelete, MdVisibility, MdDescription, MdAssignment,
  MdCheckCircle, MdFolder, MdBadge, MdLocationOn, MdPeople,
  MdChevronLeft, MdChevronRight, MdCheck,
  MdAttachFile, MdFileDownload, MdImage,
} from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const STATUS_COLOR_MAP = {
  DRAFT:        "#9CA3AF",
  SCHEDULED:    "#3B82F6",
  CONFIRMED:    "#6366F1",
  IN_PROGRESS:  "#10B981",
  COMPLETED:    "#6B7280",
  CANCELLED:    "#EF4444",
  POSTPONED:    "#F59E0B",
  RESCHEDULED:  "#8B5CF6",
};
const getStatusLabel = (statuses, code) =>
  statuses.find(s => s.statusCode === code)?.labelKh || code || "—";
const getStatusColor = (code) => STATUS_COLOR_MAP[code] || "#94A3B8";

const pad2 = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
function isMeetingLiveNow(m) {
  const status = m.statusCode || m.status || "";
  if (status === "IN_PROGRESS") return true;
  if (["CANCELLED", "COMPLETED", "POSTPONED", "DRAFT"].includes(status)) return false;
  if ((m.meetingDate || "").slice(0, 10) !== todayISO()) return false;
  const st = (m.startTime || "").slice(0, 5);
  const et = (m.endTime   || "").slice(0, 5);
  if (!st || !et) return false;
  const now = new Date();
  const cur = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
  return cur >= st && cur <= et;
}

function isGenericServerError(e) {
  try {
    const o = e?.body || JSON.parse(e?.message);
    return /INTERNAL|SERVER_ERROR/i.test(o?.code || "") ||
           /មានបញ្ហាក្នុងប្រព័ន្ធ/.test(o?.message || "");
  } catch {
    return /មានបញ្ហាក្នុងប្រព័ន្ធ|Error 500/.test(e?.message || "");
  }
}

function isRoomInUseError(e) {
  try {
    const o = e?.body || JSON.parse(e?.message);
    return /conflict|409|in.?use|cannot.?delete|referenc|constraint|foreign|integrity|meeting/i
      .test(`${o?.code || ""} ${o?.message || ""}`);
  } catch {
    return /conflict|409|constraint|integrity/i.test(e?.message || "");
  }
}

function isConflictError(e) {
  try {
    const o = e?.body || JSON.parse(e?.message);
    return /conflict|already|booked|not.?available|occupied|409/i
      .test(`${o?.code || ""} ${o?.message || ""}`);
  } catch {
    return /conflict|409/i.test(e?.message || "");
  }
}

function pickUrl(res) {
  const d = res?.data?.data ?? res?.data ?? res;
  if (!d) return null;
  if (typeof d === "string") return d;
  return d.downloadUrl || d.url || d.fileUrl || d.imageUrl || d.presignedUrl || null;
}

function minuteHasAtt(min) {
  return !!min?.attachmentUrl;
}

function minuteAttName(min) {
  if (min?.attachmentName) return min.attachmentName;
  const u = min?.attachmentUrl;
  if (typeof u === "string" && u) {
    try {
      const seg = decodeURIComponent((u.split("?")[0].split("/").pop()) || "");
      if (seg) return seg;
    } catch {}
  }
  return null;
}

const ATTENDEE_ROLES = ["ORGANIZER","PRESENTER","ATTENDEE","OBSERVER"];
const ROLE_LABEL     = {
  ORGANIZER: "អ្នករៀបចំ",
  PRESENTER: "អ្នកធ្វើបទបង្ហាញ",
  ATTENDEE:  "អ្នកចូលរួម",
  OBSERVER:  "អ្នកសង្កេតការណ៍",
};

const ATTENDANCE_LABEL = {
  INVITED:   "បានអញ្ជើញ",
  CONFIRMED: "បានបញ្ជាក់",
  ATTENDED:  "វត្តមាន",
  ABSENT:    "អវត្តមាន",
};

function Pill({ label, color }) {
  return (
    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11,
                   fontWeight:600, background:color+"22", color,
                   whiteSpace:"nowrap", fontFamily:KH }}>
      {label}
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <p style={{ fontSize:11, color:MUTED, fontWeight:600, margin:"14px 0 8px",
                borderBottom:`1px solid ${BORDER}`, paddingBottom:4,
                fontFamily:KH }}>
      {children}
    </p>
  );
}

// MeetingResponse carries roomCode/roomLocation, not roomName.
function roomLabel(m) {
  const name = m?.roomName || m?.roomCode;
  if (name) return m?.roomLocation ? `${name} · ${m.roomLocation}` : name;
  return m?.roomId ? `បន្ទប់ ${m.roomId}` : "—";
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between",
                  alignItems:"flex-start", padding:"8px 0",
                  borderBottom:`1px solid ${BORDER}` }}>
      <span style={{ fontSize:12, color:MUTED, flexShrink:0,
                     minWidth:120, fontFamily:KH }}>{label}</span>
      <span style={{ fontSize:13, color:"var(--text)", fontWeight:500,
                     textAlign:"right", fontFamily:KH }}>
        {value || "—"}
      </span>
    </div>
  );
}

function UserPicker({ users = [], peopleLoaded = false, pickedIds = [],
                      onToggle, excludeUserIds = null }) {
  const { t } = useLanguage();
  const [q, setQ] = useState("");

  const people = users.map(u => ({
    userId: u.userId ?? u.id,
    nameKh: u.userNameKh || u.officerInfo?.fullNameKh,
    nameEn: u.userNameEn || u.officerInfo?.fullNameEn,
    email:  u.email,
    code:   u.officerInfo?.code,
    dept:   u.officerInfo?.departmentName,
    type:   u.userType || u.officerInfo?.type,
  }));

  const ql = q.toLowerCase();
  const pickable = people.filter(p => {
    if (p.userId == null) return false;
    if (excludeUserIds?.has(p.userId)) return false;
    if (!ql) return true;
    return [p.nameKh, p.nameEn, p.email, p.code]
      .filter(Boolean).some(v => String(v).toLowerCase().includes(ql));
  });

  return (
    <>
      <div style={{ marginBottom:10 }}>
        <input value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={t("ស្វែងរកឈ្មោះ, អ៊ីមែល ឬ កូដ...")}
          style={{ width:"100%", padding:"9px 12px",
                   border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)",
                   fontFamily:KH, boxSizing:"border-box" }} />
      </div>

      <div style={{ border:`1px solid ${BORDER}`, borderRadius:8,
                    maxHeight:220, overflowY:"auto", marginBottom:6 }}>
        {pickable.length === 0 ? (
          <p style={{ fontSize:12, color:MUTED, textAlign:"center",
                      padding:"18px 0", fontFamily:KH }}>
            {!peopleLoaded ? "កំពុងផ្ទុក..."
              : q ? "គ្មានលទ្ធផលត្រូវនឹងការស្វែងរក"
                  : "គ្មានអ្នកប្រើដែលអាចបន្ថែម"}
          </p>
        ) : pickable.map(p => {
          const on = pickedIds.includes(p.userId);
          return (
            <label key={p.userId}
              style={{ display:"flex", alignItems:"center", gap:10,
                       padding:"9px 12px", cursor:"pointer",
                       background: on ? BG : "var(--surface)",
                       borderBottom:`1px solid ${BORDER}` }}>
              <input type="checkbox" checked={on}
                onChange={() => onToggle(p.userId)}
                style={{ width:15, height:15, accentColor:BROWN, flexShrink:0 }} />
              <div style={{ minWidth:0 }}>
                <span style={{ fontSize:13, color:"var(--text)", fontFamily:KH,
                               display:"block" }}>
                  {p.nameKh || p.nameEn || p.email || `ID: ${p.userId}`}
                </span>
                {p.dept && (
                  <span style={{ fontSize:10, color:MUTED, fontFamily:KH }}>
                    {p.dept}
                  </span>
                )}
              </div>
              {p.type && (
                <span style={{ marginLeft:"auto", flexShrink:0,
                               padding:"2px 8px", borderRadius:20, fontSize:10,
                               fontFamily:KH, fontWeight:600,
                               background: p.type === "OFFICER" ? "#EFF6FF" : "#FEF3C7",
                               color:      p.type === "OFFICER" ? "#1D4ED8" : "#92400E" }}>
                  {p.type === "OFFICER" ? "មន្ត្រី" : "មន្ត្រីកិច្ចសន្យា"}
                </span>
              )}
            </label>
          );
        })}
      </div>
      <p style={{ fontSize:11, color: pickedIds.length ? BROWN : MUTED,
                  marginBottom:14, fontFamily:KH }}>
        បានជ្រើសរើស {pickedIds.length} នាក់
      </p>
    </>
  );
}

function MeetingDetail({ meeting, onDeleted, onEdit,
                          users = [], peopleLoaded = false, meetingStatuses = [] }) {
  const { t } = useLanguage();
  const [tab,        setTab]        = useState("info");
  const [attendees,  setAttendees]  = useState([]);
  const [minutes,    setMinutes]    = useState(null);
  const [loadingAtt, setLoadingAtt] = useState(false);
  const [loadingMin, setLoadingMin] = useState(false);
  const [attBusy,    setAttBusy]    = useState(null);

  const meetId = meeting?.meetingId ?? meeting?.id;
  useEffect(() => {
    setMinutes(null);
    setAttendees([]);
    setMinAttUrl(null);
  }, [meetId]);
  const [attModal,      setAttModal]      = useState(false);
  const [minModal,      setMinModal]      = useState(false);
  const [removeAttId,   setRemoveAttId]   = useState(null);
  const [toast,         setToast]         = useState(null);
  const [err,           setErr]           = useState(null);
  const [attForm,    setAttForm]    = useState({});
  const [minForm,    setMinForm]    = useState({});
  const [minFile,    setMinFile]    = useState(null);
  const [minAttUrl,  setMinAttUrl]  = useState(null);
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    const mid2 = meeting?.meetingId ?? meeting?.id;
    if (tab === "attendees" && mid2) {
      setLoadingAtt(true);
      attendeeAPI.getAll(mid2)
        .then(r => {
          const d = r?.data?.data ?? r?.data;
          setAttendees(Array.isArray(d) ? d : []);
        })
        .catch(() => {})
        .finally(() => setLoadingAtt(false));
    }
  }, [tab, meetId]);

  useEffect(() => {
    const mid = meeting?.meetingId ?? meeting?.id;
    if (tab === "minutes" && mid) {
      setLoadingMin(true);
      meetingMinuteAPI.getAll(0, 1, mid)
        .then(async r => {
          const d = r?.data?.data ?? r?.data;
          const list = d?.content || (Array.isArray(d) ? d : []);
          const min = list[0] || null;
          setMinutes(min);
          const url = await resolveMinuteAttachment(min);
          setMinAttUrl(url);
        })
        .catch(() => {})
        .finally(() => setLoadingMin(false));
    }
  }, [tab, meetId]);

  const resolveMinuteAttachment = async (min, { force = false } = {}) => {
    if (!min) return null;
    if (!force && !min.attachmentUrl) return null;
    const minId = min.minuteId ?? min.id;
    if (minId) {
      try {
        const url = pickUrl(await meetingMinuteAPI.getDownloadUrl(minId));
        if (url) return url;
      } catch {}
    }
    return /^https?:\/\//i.test(min.attachmentUrl || "") ? min.attachmentUrl : null;
  };

  const toggleUserPick = (uid) => {
    setAttForm(p => {
      const ids = p.userIds || [];
      return {
        ...p,
        userIds: ids.includes(uid) ? ids.filter(x => x !== uid) : [...ids, uid],
      };
    });
  };

  const handleAddAttendees = async () => {
    const userIds = attForm.userIds || [];
    const mid = meeting?.meetingId ?? meeting?.id;
    if (userIds.length === 0) { setErr(t("សូមជ្រើសរើសអ្នកប្រើប្រាស់យ៉ាងតិចម្នាក់")); return; }
    if (!mid)                 { setErr(t("រកមិនឃើញកិច្ចប្រជុំ")); return; }

    setSaving(true);
    try {
      await meetingAPI.addAttendeesBulk(mid, {
        userIds,
        role: attForm.role || "ATTENDEE",
      });
      const r = await attendeeAPI.getAll(mid);
      const d = r?.data?.data ?? r?.data;
      setAttendees(Array.isArray(d) ? d : []);
      setToast(`បន្ថែមអ្នករួមចំណែក ${userIds.length} នាក់ បានជោគជ័យ!`);
      setAttModal(false); setAttForm({});
    } catch (e) {
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleAttendance = async (a) => {
    const attendeeId = a.attendeeId ?? a.id;
    const mid = meeting?.meetingId ?? meeting?.id;
    if (!attendeeId || !mid) return;
    const prevStatus = a.attendanceStatus;
    const next = prevStatus === "ATTENDED" ? "ABSENT" : "ATTENDED";
    setAttBusy(attendeeId);
    setAttendees(prev => prev.map(x =>
      (x.attendeeId ?? x.id) === attendeeId ? { ...x, attendanceStatus: next } : x));
    try {
      await meetingAPI.updateAttendance(mid, attendeeId, { attendanceStatus: next });
    } catch (e) {
      setAttendees(prev => prev.map(x =>
        (x.attendeeId ?? x.id) === attendeeId ? { ...x, attendanceStatus: prevStatus } : x));
      setErr(errMessage(e));
    } finally {
      setAttBusy(null);
    }
  };

  const handleRemoveAttendee = (attendeeId) => setRemoveAttId(attendeeId);

  const doRemoveAttendee = async () => {
    const attendeeId = removeAttId;
    setRemoveAttId(null);
    try {
      await attendeeAPI.remove(meeting.meetingId ?? meeting.id, attendeeId);
      setAttendees(attendees.filter(a => (a.attendeeId ?? a.id) !== attendeeId));
      setToast(t("លុបអ្នករួមចំណែកបានជោគជ័យ!"));
    } catch (e) { setErr(errMessage(e)); }
  };

  const handleSaveMinutes = async () => {
    setSaving(true);
    try {
      const meetId = meeting.meetingId ?? meeting.id;
      const payload = {
        meetingId:   meetId,
        summary:     minForm.summary     || "",
        decisions:   minForm.decisions   || "",
        actionItems: minForm.actionItems || "",
      };

      let savedMinute = minutes;
      const minuteId = minutes?.minuteId ?? minutes?.id;
      if (minuteId) {
        await meetingMinuteAPI.update(minuteId, payload);
      } else {
        const createRes = await meetingMinuteAPI.create(payload);
        const d = createRes?.data?.data ?? createRes?.data;
        savedMinute = d;
      }

      const uploadMinuteId = savedMinute?.minuteId ?? savedMinute?.id;
      if (minFile && uploadMinuteId) {
        try {
          await meetingMinuteAPI.uploadFile(uploadMinuteId, minFile);
        } catch (uploadErr) {
          console.warn("File upload failed:", uploadErr.message);
          setErr("រក្សាទុករួចហើយ ប៉ុន្តែការបញ្ចូលឯកសារបរាជ័យ: " + errMessage(uploadErr));
        }
      }
      const reloadMid = meeting.meetingId ?? meeting.id;
      const r = await meetingMinuteAPI.getAll(0, 1, reloadMid);
      const rd = r?.data?.data ?? r?.data;
      const rlist = rd?.content || (Array.isArray(rd) ? rd : []);
      const savedMin = rlist[0] || null;
      setMinutes(savedMin);

      const url = await resolveMinuteAttachment(
        savedMin || { minuteId: uploadMinuteId },
        { force: !!minFile }
      );
      setMinAttUrl(url);

      setToast(t("រក្សាទុកកំណត់ហេតុបានជោគជ័យ!"));
      setMinModal(false); setMinForm({}); setMinFile(null);
    } catch (e) {
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (!meeting) return null;

  const sc = getStatusColor(meeting.statusCode || meeting.status);
  const sl = getStatusLabel(meetingStatuses, meeting.statusCode || meeting.status);

  const tabs = [
    { key:"info",      label:"ព័ត៌មានលម្អិត"  },
    { key:"attendees", label:"អ្នករួមចំណែក"   },
    { key:"minutes",   label:"ភ្ជាប់ឯកសារ"    },
  ];

  return (
    <div style={{ background:"var(--surface)", borderRadius:12,
                  border:`1px solid ${BORDER}`, overflow:"hidden",
                  position:"sticky", top:16 }}>

      <div style={{ background:BROWN, padding:"16px 20px" }}>
        <h4 style={{ fontSize:15, fontWeight:700, color:"#fff",
                     fontFamily:MO, marginBottom:6, lineHeight:1.4 }}>
          {meeting.title || "ប្រជុំ"}
        </h4>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:8 }}>
          <Pill label={sl} color={sc} />
          {meeting.meetingType && (
            <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11,
                           fontWeight:600, background:"rgba(255,255,255,0.15)",
                           color:"#fff", fontFamily:KH }}>
              {meeting.meetingType}
            </span>
          )}
        </div>
        <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
          <span style={{ fontSize:11, color:"rgba(255,255,255,0.75)",
                         fontFamily:KH, display:"flex", alignItems:"center", gap:4 }}>
            <MdCalendarToday size={11}/> {meeting.meetingDate || "—"} · {(meeting.startTime||"")?.slice(0,5)} – {(meeting.endTime||"")?.slice(0,5)}
          </span>
          <span style={{ fontSize:11, color:"rgba(255,255,255,0.75)",
                         fontFamily:KH, display:"flex", alignItems:"center", gap:4 }}>
            <MdMeetingRoom size={11}/> {roomLabel(meeting)}
          </span>
        </div>
      </div>

      <div style={{ padding:20 }}>
        <div style={{ display:"flex", gap:0, marginBottom:16,
                      borderBottom:`2px solid ${BORDER}` }}>
          {tabs.map(tb => (
            <button key={tb.key} onClick={() => setTab(tb.key)} style={{
              padding:"10px 18px", fontSize:13, cursor:"pointer",
              fontFamily:KH, background:"transparent", border:"none",
              color:      tab===tb.key ? BROWN   : MUTED,
              fontWeight: tab===tb.key ? 700     : 400,
              borderBottom: tab===tb.key
                ? `2px solid ${BROWN}`
                : "2px solid transparent",
              marginBottom:"-2px", transition:"all 0.15s",
            }}>{t(tb.label)}</button>
          ))}
        </div>

      {tab === "info" && (
        <div>
          <InfoRow label={t("ថ្ងៃ និងម៉ោង")}
            value={`${meeting.meetingDate || "—"} ${(meeting.startTime || "")?.slice(0,5)}`} />
          <InfoRow label={t("ស្ថានភាព")}
            value={<Pill label={sl} color={sc} />} />
          <InfoRow label={t("បន្ទប់")}
            value={roomLabel(meeting)} />
          <InfoRow label={t("អ្នករៀបចំ")}
            value={meeting.organizerName || meeting.organizerId || "—"} />
          <InfoRow label={t("ប្រភេទប្រជុំ")}
            value={meeting.meetingType || "—"} />
          {meeting.meetingLink && (
            <InfoRow label={t("តំណភ្ជាប់")}
              value={
                <a href={meeting.meetingLink} target="_blank" rel="noreferrer"
                  style={{ color:BROWN, fontSize:12 }}>
                  {meeting.meetingLink}
                </a>
              } />
          )}
          {meeting.description && (
            <div style={{ marginTop:10 }}>
              <p style={{ fontSize:11, color:MUTED, marginBottom:4,
                          fontFamily:KH }}>Agenda</p>
              <p style={{ fontSize:13, color:"var(--text)", fontFamily:KH,
                          lineHeight:1.6, whiteSpace:"pre-wrap" }}>
                {meeting.description}
              </p>
            </div>
          )}

          {(() => {
            const status = meeting.statusCode || meeting.status || "";
            const canEdit = ["DRAFT","SCHEDULED","CONFIRMED","POSTPONED","RESCHEDULED"].includes(status);
            return (
              <div style={{ marginTop:20, display:"flex",
                            justifyContent:"space-between", alignItems:"center" }}>
                {canEdit ? (
                  <Can perm="meeting" action="edit">
                  <Btn variant="outline"
                    onClick={() => onEdit && onEdit(meeting)}
                    style={{display:"flex",alignItems:"center",gap:5}}>
                    <MdEdit size={14}/> {t("កែប្រែ")}
                  </Btn>
                  </Can>
                ) : (
                  <div style={{ fontSize:11, color:MUTED, fontFamily:KH,
                                padding:"6px 12px", background:BG,
                                borderRadius:6, border:`1px solid ${BORDER}`,
                                display:"flex", alignItems:"center", gap:5 }}>
                    <MdLock size={13}/> មិនអាចកែប្រែ — {getStatusLabel(meetingStatuses, status)}
                  </div>
                )}
                <Can perm="meeting" action="delete">
                <Btn variant="danger"
                  onClick={() => onDeleted(meeting.meetingId ?? meeting.id)}
                  style={{display:"flex",alignItems:"center",gap:5}}>
                  <MdDelete size={14}/> {t("លុបប្រជុំ")}
                </Btn>
                </Can>
              </div>
            );
          })()}
        </div>
      )}

      {tab === "attendees" && (
        <div>
          <SectionLabel>
            {t("ភេទ/ព្រះនាម &nbsp;|&nbsp; តួនាទី &nbsp;|&nbsp; វត្តមាន")}
          </SectionLabel>

          {loadingAtt ? (
            <p style={{ fontSize:12, color:MUTED, fontFamily:KH }}>
              {t("កំពុងផ្ទុក...")}
            </p>
          ) : attendees.length === 0 ? (
            <p style={{ fontSize:12, color:MUTED, textAlign:"center",
                        padding:"20px 0", fontFamily:KH }}>
              {t("គ្មានអ្នករួមចំណែក")}
            </p>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
              <div style={{ display:"grid",
                            gridTemplateColumns:"1fr 1fr 1fr auto",
                            gap:8, padding:"6px 0",
                            borderBottom:`1px solid ${BORDER}`,
                            fontSize:11, color:MUTED, fontFamily:KH }}>
                <span>{t("ភេទ/ព្រះនាម")}</span>
                <span>{t("តួនាទី")}</span>
                <span>{t("វត្តមាន")}</span>
                <span></span>
              </div>
              {attendees.map(a => (
                <div key={a.attendeeId ?? a.id}
                  style={{ display:"grid",
                            gridTemplateColumns:"1fr 1fr 1fr auto",
                            gap:8, padding:"10px 0",
                            borderBottom:`1px solid ${BORDER}`,
                            alignItems:"center" }}>
                  <span style={{ fontSize:13, color:"var(--text)",
                                 fontFamily:KH }}>
                    {a.userName || a.officerName || "—"}
                  </span>
                  <span style={{ fontSize:12, color:MUTED,
                                 fontFamily:KH }}>
                    {a.roleLabel || ROLE_LABEL[a.role] || a.role || "—"}
                  </span>
                  <label style={{ display:"flex", alignItems:"center", gap:6,
                                  cursor: attBusy === (a.attendeeId ?? a.id) ? "wait" : "pointer" }}>
                    <input type="checkbox"
                      checked={a.attendanceStatus === "ATTENDED"}
                      disabled={attBusy === (a.attendeeId ?? a.id)}
                      onChange={() => toggleAttendance(a)}
                      style={{ width:15, height:15, accentColor:BROWN, cursor:"inherit" }} />
                    <span style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                      {ATTENDANCE_LABEL[a.attendanceStatus] || a.attendanceLabel || "អវត្តមាន"}
                    </span>
                  </label>
                  <Can perm="meeting" action="edit">
                    <button onClick={() => handleRemoveAttendee(a.attendeeId ?? a.id)}
                      style={{ background:"none", border:"none",
                               cursor:"pointer", color:"#EF4444",
                               display:"flex", alignItems:"center" }}>
                      <MdDelete size={16}/>
                    </button>
                  </Can>
                </div>
              ))}
            </div>
          )}

          <button onClick={() => { setAttForm({}); setAttModal(true); }}
            style={{ marginTop:14, padding:"8px 16px",
                     background:BG, border:`1px solid ${BORDER}`,
                     borderRadius:8, cursor:"pointer",
                     fontSize:12, color:BROWN, fontFamily:KH }}>
            {t("+ បន្ថែមអ្នករួមចំណែក")}
          </button>
        </div>
      )}

      {tab === "minutes" && (
        <div>
          {loadingMin ? (
            <p style={{ fontSize:12, color:MUTED, fontFamily:KH,
                        padding:"20px 0", textAlign:"center" }}>
              {t("កំពុងផ្ទុក...")}
            </p>
          ) : minutes ? (
            <div>
              <p style={{ fontSize:11, color:MUTED, fontWeight:600,
                          marginBottom:6, fontFamily:KH }}>{t("សេចក្តីសង្ខេប")}</p>
              <div style={{ background:BG, borderRadius:8, padding:12,
                            fontSize:13, color:"var(--text)", fontFamily:KH,
                            lineHeight:1.7, marginBottom:12,
                            whiteSpace:"pre-wrap", minHeight:40 }}>
                {minutes.summary || "—"}
              </div>

              <p style={{ fontSize:11, color:MUTED, fontWeight:600,
                          marginBottom:6, fontFamily:KH }}>{t("សេចក្តីសម្រេច")}</p>
              <div style={{ background:BG, borderRadius:8, padding:12,
                            fontSize:13, color:"var(--text)", fontFamily:KH,
                            lineHeight:1.7, marginBottom:12,
                            whiteSpace:"pre-wrap", minHeight:40 }}>
                {minutes.decisions || "—"}
              </div>

              {minutes.actionItems && (
                <>
                  <p style={{ fontSize:11, color:MUTED, fontWeight:600,
                              marginBottom:6, fontFamily:KH }}>{t("កិច្ចការត្រូវបន្ត")}</p>
                  <div style={{ background:BG, borderRadius:8, padding:12,
                                fontSize:13, color:"var(--text)", fontFamily:KH,
                                lineHeight:1.7, marginBottom:12,
                                whiteSpace:"pre-wrap" }}>
                    {minutes.actionItems}
                  </div>
                </>
              )}

              {(minAttUrl || minuteHasAtt(minutes)) && (
                <div style={{ marginBottom:14 }}>
                  <p style={{ fontSize:11, color:MUTED, fontWeight:600,
                              marginBottom:8, fontFamily:KH,
                              display:"flex", alignItems:"center", gap:4 }}>
                    <MdAttachFile size={14}/> {t("ឯកសារភ្ជាប់")}</p>
                  {minAttUrl ? (() => {
                    const probe = `${minuteAttName(minutes) || ""} ${minAttUrl}`;
                    const isImg = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|#|$|\s)/i.test(probe);
                    return isImg ? (
                      <div style={{ textAlign:"center", marginBottom:8 }}>
                        <img src={minAttUrl} alt="attachment"
                          style={{ maxWidth:"100%", maxHeight:280,
                                   borderRadius:8, border:`1px solid ${BORDER}`,
                                   objectFit:"contain" }} />
                        <a href={minAttUrl} target="_blank" rel="noreferrer"
                          style={{ display:"inline-flex", alignItems:"center", gap:4, marginTop:8,
                                   fontSize:12, color:BROWN, fontFamily:KH }}>
                          <MdFileDownload size={15}/> {t("ទាញយករូបភាព")}
                        </a>
                      </div>
                    ) : (
                      <a href={minAttUrl} target="_blank" rel="noreferrer"
                        style={{ display:"flex", alignItems:"center", gap:10,
                                 padding:"12px 14px", background:"#EDE9FE",
                                 borderRadius:8, textDecoration:"none" }}>
                        <span style={{ fontSize:24, display:"flex", alignItems:"center", color:"#5B21B6" }}><MdDescription size={24}/></span>
                        <div>
                          <p style={{ fontSize:12, color:"#5B21B6", fontFamily:KH,
                                      fontWeight:600, marginBottom:2 }}>
                            {minuteAttName(minutes) || "ឯកសារភ្ជាប់"}
                          </p>
                          <p style={{ fontSize:11, color:MUTED, fontFamily:KH,
                                      display:"flex", alignItems:"center", gap:4 }}>
                            {t("ចុចដើម្បីបើក / ទាញយក")} <MdFileDownload size={13}/>
                          </p>
                        </div>
                      </a>
                    );
                  })() : (
                    <div style={{ display:"flex", alignItems:"center", gap:10,
                                  padding:"12px 14px", background:BG,
                                  borderRadius:8, border:`1px solid ${BORDER}` }}>
                      <span style={{ display:"flex", alignItems:"center", color:MUTED }}><MdAttachFile size={20}/></span>
                      <p style={{ fontSize:12, color:MUTED, fontFamily:KH }}>
                        {minuteAttName(minutes) || "ឯកសារភ្ជាប់"} — កំពុងផ្ទុក URL...
                      </p>
                    </div>
                  )}
                </div>
              )}

              {minutes.recordedByName && (
                <div style={{ display:"flex", alignItems:"center",
                              gap:8, marginBottom:12 }}>
                  <span style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                    {t("កត់ត្រាដោយ:")}
                  </span>
                  <Pill label={minutes.recordedByName} color={BROWN} />
                </div>
              )}

              <Can perm="meeting" action="edit">
                <button onClick={() => {
                    setMinForm({
                      summary:     minutes.summary     || "",
                      decisions:   minutes.decisions   || "",
                      actionItems: minutes.actionItems || "",
                    });
                    setMinFile(null);
                    setMinModal(true);
                  }}
                  style={{ padding:"8px 18px", background:BROWN, color:"#fff",
                           border:"none", borderRadius:8, cursor:"pointer",
                           fontSize:12, fontFamily:KH,
                  display:"flex", alignItems:"center", gap:5 }}>
                  <MdEdit size={13}/> {t("កែប្រែកំណត់ហេតុ")}
                </button>
              </Can>
            </div>
          ) : (
            <div style={{ textAlign:"center", padding:"30px 0" }}>
              <div style={{ fontSize:40, marginBottom:12, display:"flex", justifyContent:"center", color:MUTED }}><MdAssignment size={40}/></div>
              <p style={{ fontSize:13, color:MUTED, marginBottom:16,
                          fontFamily:KH }}>
                {t("មិនទាន់មានកំណត់ហេតុប្រជុំ")}
              </p>
              <button onClick={() => {
                  setMinForm({ summary:"", decisions:"", actionItems:"" });
                  setMinFile(null);
                  setMinModal(true);
                }}
                style={{ padding:"10px 22px", background:BROWN, color:"#fff",
                         border:"none", borderRadius:8, cursor:"pointer",
                         fontSize:13, fontFamily:KH }}>
                {t("+ បន្ថែមកំណត់ហេតុប្រជុំ")}
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={removeAttId !== null}
        message={t("តើអ្នកពិតជាចង់លុបអ្នករួមចំណែកនេះមែនទេ?")}
        onConfirm={doRemoveAttendee}
        onCancel={() => setRemoveAttId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {attModal && (() => {
        const pickedIds    = attForm.userIds || [];
        const attendingIds = new Set(attendees.map(a => a.userId).filter(Boolean));
        return (
        <Modal
          title={t("បន្ថែមអ្នករួមចំណែក")}
          subtitle={t("អាចជ្រើសរើសបានច្រើននាក់ក្នុងពេលតែមួយ")}
          onClose={() => { setAttModal(false); setAttForm({}); }}
        >
          <UserPicker
            users={users}
            peopleLoaded={peopleLoaded}
            pickedIds={pickedIds}
            onToggle={toggleUserPick}
            excludeUserIds={attendingIds}
          />

          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED,
                            marginBottom:4, fontFamily:KH }}>
              {t("តួនាទី (សម្រាប់អ្នកជ្រើសរើសទាំងអស់)")}
            </label>
            <select value={attForm.role||""}
              onChange={e => setAttForm(p=>({...p, role:e.target.value}))}
              style={{ width:"100%", padding:"9px 12px",
                       border:`1px solid ${BORDER}`, borderRadius:8,
                       fontSize:13, outline:"none", background:"var(--surface)",
                       fontFamily:KH, boxSizing:"border-box" }}>
              <option value="">{t("ជ្រើសរើស តួនាទី")}</option>
              {ATTENDEE_ROLES.map(r => (
                <option key={r} value={r}>{ROLE_LABEL[r]}</option>
              ))}
            </select>
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end",
                        gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setAttModal(false); setAttForm({}); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleAddAttendees}
              style={{ opacity: (saving || pickedIds.length === 0) ? 0.6 : 1 }}>
              {saving ? "កំពុង..."
                      : `បន្ថែម ${pickedIds.length ? pickedIds.length + " នាក់" : ""}`}
            </Btn>
          </div>
        </Modal>
        );
      })()}

      {minModal && (
        <Modal
          title={minutes?.minuteId ?? minutes?.id ? "កែប្រែកំណត់ហេតុប្រជុំ" : "បន្ថែមកំណត់ហេតុប្រជុំ"}
          subtitle={meeting.title}
          onClose={() => { setMinModal(false); setMinForm({}); setMinFile(null); }}
        >
          <FormField label={t("សេចក្តីសង្ខេប")}
            value={minForm.summary||""}
            onChange={v=>setMinForm(p=>({...p,summary:v}))}
            rows={3} placeholder={t("សង្ខេបដំណើរការ និងចំណុចសំខាន់ៗ...")} />

          <FormField label={t("សេចក្តីសម្រេច")}
            value={minForm.decisions||""}
            onChange={v=>setMinForm(p=>({...p,decisions:v}))}
            rows={3} placeholder={t("អ្វីដែលបានសម្រេចក្នុងកិច្ចប្រជុំ...")} />

          <FormField label={t("កិច្ចការត្រូវបន្ត")}
            value={minForm.actionItems||""}
            onChange={v=>setMinForm(p=>({...p,actionItems:v}))}
            rows={2} placeholder={t("បញ្ជីការងារត្រូវអនុវត្ត...")} />

          <div style={{ marginBottom:14 }}>
            <label style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, color:MUTED,
                            marginBottom:6, fontFamily:KH, fontWeight:600 }}>
              <MdAttachFile size={15}/> {t("ឯកសារភ្ជាប់")}
            </label>

            {!minFile && (minuteHasAtt(minutes) || minAttUrl) && (
              <div style={{ display:"flex", alignItems:"center", gap:8,
                            padding:"8px 12px", background:"#EDE9FE",
                            borderRadius:8, marginBottom:8 }}>
                <MdDescription size={16} style={{ color:"#5B21B6", flexShrink:0 }}/>
                <span style={{ fontSize:12, color:"#5B21B6", fontFamily:KH }}>
                  {minuteAttName(minutes) || "ឯកសារភ្ជាប់"}
                </span>
                <span style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                  {t("— (ឯកសារបច្ចុប្បន្ន)")}
                </span>
              </div>
            )}

            <label style={{
              display:"flex", alignItems:"center", gap:12,
              padding:"14px 16px", border:`2px dashed ${BORDER}`,
              borderRadius:8, background: minFile ? "#F0FDF4" : BG,
              cursor:"pointer", transition:"all 0.2s",
            }}>
              <span style={{ fontSize:28, display:"flex", alignItems:"center", color:MUTED }}>
                {minFile ? <MdCheckCircle size={28} color="#10B981"/> : <MdFolder size={28}/>}
              </span>
              <div style={{ flex:1 }}>
                {minFile ? (
                  <div>
                    <p style={{ fontSize:13, fontWeight:600, color:"#065F46",
                                fontFamily:KH, marginBottom:2 }}>
                      {minFile.name}
                    </p>
                    <p style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                      {(minFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p style={{ fontSize:13, color:BROWN, fontFamily:KH,
                                marginBottom:2 }}>
                      {t("ចុចដើម្បីជ្រើសរើសឯកសារ")}
                    </p>
                    <p style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                      {t("PDF, Word, JPG, PNG (អតិបរមា 10MB)")}
                    </p>
                  </div>
                )}
              </div>
              {minFile && (
                <button onClick={e => { e.preventDefault(); setMinFile(null); }}
                  style={{ background:"#FEE2E2", border:"none", borderRadius:6,
                           padding:"4px 8px", cursor:"pointer",
                           color:"#991B1B", fontSize:12 }}>
                  {t("លុប")}
                </button>
              )}
              <input type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={e => setMinFile(e.target.files[0] || null)}
                style={{ display:"none" }} />
            </label>
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end",
                        gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setMinModal(false); setMinForm({}); setMinFile(null); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSaveMinutes}
              style={{ opacity: saving ? 0.7 : 1 }}>
              {saving ? "កំពុង..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}
      </div>
    </div>
  );
}

const DAY_START = 8 * 60;
const DAY_END   = 18 * 60;
const SLOT_MIN  = 30;
const N_SLOTS   = (DAY_END - DAY_START) / SLOT_MIN;
const GRID_ROW_H  = 42;
const GRID_LABEL_W = 128;
const SLOT_W      = 38;

const KH_DAYS   = ["អាទិត្យ","ច័ន្ទ","អង្គារ","ពុធ","ព្រហស្បតិ៍","សុក្រ","សៅរ៍"];
const KH_MONTHS = ["មករា","កុម្ភៈ","មីនា","មេសា","ឧសភា","មិថុនា",
                   "កក្កដា","សីហា","កញ្ញា","តុលា","វិច្ឆិកា","ធ្នូ"];

const isoToDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const dateToISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const khDate = (iso) => {
  const d = isoToDate(iso);
  return `${KH_DAYS[d.getDay()]} ${String(d.getDate()).padStart(2,"0")} ${KH_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const khDateShort = (iso) => {
  const d = isoToDate(iso);
  return `${String(d.getDate()).padStart(2,"0")} ${KH_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const timeToMin = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(":").map(Number);
  return Number.isNaN(h) ? null : h * 60 + (m || 0);
};
const slotLabel = (idx) => {
  const min = DAY_START + idx * SLOT_MIN;
  return `${String(Math.floor(min/60)).padStart(2,"0")}:${String(min%60).padStart(2,"0")}`;
};

function RoomAvailabilityGrid({ rooms, date, value, onSelect, onDateChange,
                                excludeMeetingId, canBook, refreshKey }) {
  const { t } = useLanguage();
  const [bookings, setBookings] = useState({});
  const [loading,  setLoading]  = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!date || rooms.length === 0) { setBookings({}); return; }
    let stale = false;
    setLoading(true);

    const normalize = (m) => ({
      meetingId: m.meetingId ?? m.id ?? null,
      title:     m.title || m.meetingTitle || "កិច្ចប្រជុំ",
      organizer: m.organizerName || "",
      startTime: m.startTime,
      endTime:   m.endTime,
      status:    m.statusCode || m.status || "",
      date:      (m.meetingDate || m.date || "").slice(0, 10),
      cancelled: m.cancelled === true,
    });

    Promise.allSettled(
      rooms.map(rm => {
        const id = rm.roomId ?? rm.id;
        return Promise.allSettled([
          meetingAPI.getRoomSchedule(id, date),
          meetingRoomAPI.getSchedule(id, date),
        ]);
      })
    ).then(results => {
      if (stale) return;
      const byRoom = {};
      rooms.forEach((rm, i) => {
        const id = rm.roomId ?? rm.id;
        byRoom[id] = [];
        if (results[i].status !== "fulfilled") return;
        const [meetRes, roomRes] = results[i].value;

        const items = [];
        if (meetRes.status === "fulfilled") {
          const d = meetRes.value?.data?.data ?? meetRes.value?.data;
          const list = Array.isArray(d) ? d : (d?.content || []);
          list.forEach(m => items.push(normalize(m)));
        } else {
          console.warn(`Room ${id} /meetings schedule failed:`, meetRes.reason?.message);
        }
        if (roomRes.status === "fulfilled") {
          const d = roomRes.value?.data?.data ?? roomRes.value?.data;
          (d?.bookings || []).forEach(b => items.push(normalize(b)));
        } else {
          console.warn(`Room ${id} /meeting-rooms schedule failed:`, roomRes.reason?.message);
        }

        const seen = new Set();
        byRoom[id] = items
          .filter(m => !m.cancelled && m.status !== "CANCELLED")
          .filter(m => !m.date || m.date === date)
          .filter(m => excludeMeetingId == null || m.meetingId !== excludeMeetingId)
          .filter(m => {
            const key = m.meetingId ?? `${m.startTime}-${m.endTime}-${m.title}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map(m => {
            const s = timeToMin(m.startTime), e = timeToMin(m.endTime);
            if (s == null || e == null) return null;
            const startIdx = Math.max(0, Math.floor((s - DAY_START) / SLOT_MIN));
            const endIdx   = Math.min(N_SLOTS, Math.ceil((e - DAY_START) / SLOT_MIN));
            if (endIdx <= 0 || startIdx >= N_SLOTS || endIdx <= startIdx) return null;
            return {
              startIdx, span: endIdx - startIdx,
              title: m.title,
              organizer: m.organizer,
              time: `${String(m.startTime).slice(0,5)}–${String(m.endTime).slice(0,5)}`,
            };
          })
          .filter(Boolean);
      });
      setBookings(byRoom);
      setLoading(false);
    });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, date, excludeMeetingId, refreshKey]);

  const today   = dateToISO(new Date());
  const isPastDay = date && date < today;
  const isToday   = date === today;
  const nowMin  = new Date().getHours() * 60 + new Date().getMinutes();
  const masks = useMemo(() => rooms.map(rm => {
    const id = rm.roomId ?? rm.id;
    const occupied = Array(N_SLOTS).fill(false);
    (bookings[id] || []).forEach(b => {
      for (let i = b.startIdx; i < b.startIdx + b.span; i++) occupied[i] = true;
    });
    const past = Array(N_SLOTS).fill(false).map((_, i) => {
      if (isPastDay) return true;
      if (!isToday) return false;
      return DAY_START + (i + 1) * SLOT_MIN <= nowMin;
    });
    return { occupied, past };
  }), [rooms, bookings, isPastDay, isToday, nowMin]);

  const isFree = (roomIdx, i) =>
    i >= 0 && i < N_SLOTS && !masks[roomIdx]?.occupied[i] && !masks[roomIdx]?.past[i];

  const clampToFree = (roomIdx, a, b) => {
    let lo = a, hi = a;
    while (isFree(roomIdx, lo - 1)) lo--;
    while (isFree(roomIdx, hi + 1)) hi++;
    return Math.max(lo, Math.min(hi, b));
  };

  const selRoomIdx = value?.roomId
    ? rooms.findIndex(rm => String(rm.roomId ?? rm.id) === String(value.roomId))
    : -1;
  const selStartMin = timeToMin(value?.startTime);
  const selEndMin   = timeToMin(value?.endTime);
  const selLo = selStartMin != null ? Math.floor((selStartMin - DAY_START) / SLOT_MIN) : null;
  const selHi = selEndMin   != null ? Math.ceil((selEndMin   - DAY_START) / SLOT_MIN) - 1 : null;
  const hasSel = selRoomIdx >= 0 && selLo != null && selHi != null &&
                 selHi >= 0 && selLo < N_SLOTS && selHi >= selLo;

  const commit = (roomIdx, a, b) => {
    const lo = Math.min(a, b), hi = Math.max(a, b);
    const rm = rooms[roomIdx];
    onSelect(String(rm.roomId ?? rm.id), slotLabel(lo), slotLabel(hi + 1));
  };

  const onCellDown = (roomIdx, i) => {
    if (!canBook || !isFree(roomIdx, i)) return;
    if (hasSel && selRoomIdx === roomIdx && selLo === selHi && i > selLo &&
        clampToFree(roomIdx, selLo, i) === i) {
      commit(roomIdx, selLo, i);
      return;
    }
    dragRef.current = { roomIdx, anchor: i };
    commit(roomIdx, i, i);
  };
  const onCellEnter = (roomIdx, i) => {
    const drag = dragRef.current;
    if (!drag || drag.roomIdx !== roomIdx) return;
    commit(roomIdx, drag.anchor, clampToFree(roomIdx, drag.anchor, i));
  };
  useEffect(() => {
    const up = () => { dragRef.current = null; };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);

  const shiftDay = (delta) => {
    const d = date ? isoToDate(date) : new Date();
    d.setDate(d.getDate() + delta);
    onDateChange(dateToISO(d));
  };

  const navBtn = {
    width:28, height:28, borderRadius:6, border:`1px solid ${BORDER}`,
    background:"var(--surface)", cursor:"pointer", display:"flex",
    alignItems:"center", justifyContent:"center", color:BROWN, padding:0,
  };
  const legendDot = (bg, bd) => ({
    width:12, height:12, borderRadius:3, background:bg,
    border:`1px solid ${bd}`, flexShrink:0,
  });

  return (
    <div style={{ marginBottom:14 }}>
      <style>{`.rag-free:hover { background:#A7F3D0 !important; }`}</style>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8,
                    flexWrap:"wrap" }}>
        <button type="button" onClick={() => shiftDay(-1)} style={navBtn} title={t("ថ្ងៃមុន")}>
          <MdChevronLeft size={17} />
        </button>
        <button type="button" onClick={() => shiftDay(1)} style={navBtn} title={t("ថ្ងៃបន្ទាប់")}>
          <MdChevronRight size={17} />
        </button>
        <span style={{ fontSize:13, fontWeight:600, color:BROWN, fontFamily:KH }}>
          {date ? khDate(date) : "ជ្រើសរើសថ្ងៃប្រជុំ"}
        </span>
        <div style={{ marginLeft:"auto", display:"flex", gap:12, alignItems:"center",
                      fontSize:11, color:MUTED, fontFamily:KH, flexWrap:"wrap" }}>
          <span style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={legendDot("#3B82F6","#2563EB")} /> {t("កក់រួច")}
          </span>
          <span style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={legendDot("#10B981","#059669")} /> {t("ជម្រើសរបស់អ្នក")}
          </span>
          <span style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={legendDot("#F0FDF4", "#6EE7B7")} /> {t("ទំនេរ")}
          </span>
          <span style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={legendDot("#EFEAE4", "#D6CCC0")} /> {t("មិនអាចកក់បាន")}
          </span>
        </div>
      </div>

      <div style={{ border:`1px solid ${BORDER}`, borderRadius:10, overflowX:"auto" }}>
        <div style={{ width:GRID_LABEL_W + N_SLOTS * SLOT_W, userSelect:"none" }}>
          <div style={{ display:"flex", borderBottom:`1px solid ${BORDER}`, background:BG }}>
            <div style={{ width:GRID_LABEL_W, flexShrink:0, padding:"7px 10px",
                          fontSize:11, color:MUTED, fontFamily:KH, fontWeight:600,
                          position:"sticky", left:0, background:BG, zIndex:4 }}>
              {t("បន្ទប់ / ម៉ោង")}
            </div>
            <div style={{ flex:1, display:"grid",
                          gridTemplateColumns:`repeat(${N_SLOTS}, ${SLOT_W}px)` }}>
              {Array.from({ length: N_SLOTS / 2 }, (_, h) => (
                <div key={h} style={{ gridColumn:"span 2", padding:"7px 0 7px 4px",
                                      fontSize:10, color:MUTED,
                                      borderLeft:`1px solid ${BORDER}` }}>
                  {8 + h}:00
                </div>
              ))}
            </div>
          </div>

          {rooms.length === 0 ? (
            <p style={{ padding:18, textAlign:"center", color:MUTED,
                        fontSize:12, fontFamily:KH }}>
              {t("គ្មានបន្ទប់ប្រជុំ")}
            </p>
          ) : rooms.map((rm, roomIdx) => {
            const id = rm.roomId ?? rm.id;
            const mask = masks[roomIdx] || { occupied:[], past:[] };
            return (
              <div key={id} style={{ display:"flex",
                                     borderBottom:`1px solid ${BORDER}` }}>
                <div style={{ width:GRID_LABEL_W, flexShrink:0, padding:"5px 10px",
                              borderRight:`1px solid ${BORDER}`, overflow:"hidden",
                              background:BG, position:"sticky", left:0, zIndex:4 }}>
                  <p style={{ fontSize:12, fontWeight:600, color:BROWN, fontFamily:KH,
                              overflow:"hidden", textOverflow:"ellipsis",
                              whiteSpace:"nowrap" }}>
                    {rm.roomCode || `បន្ទប់ ${id}`}
                  </p>
                  <p style={{ fontSize:10, color:MUTED, fontFamily:KH,
                              overflow:"hidden", textOverflow:"ellipsis",
                              whiteSpace:"nowrap" }}>
                    {rm.capacity != null ? `${rm.capacity} នាក់` : ""}
                    {rm.capacity != null && rm.location ? " • " : ""}
                    {rm.location || ""}
                  </p>
                </div>

                <div style={{ flex:1, display:"grid", height:GRID_ROW_H,
                              gridTemplateColumns:`repeat(${N_SLOTS}, ${SLOT_W}px)`,
                              gridTemplateRows:"1fr" }}>
                  {Array.from({ length: N_SLOTS }, (_, i) => {
                    const free = isFree(roomIdx, i);
                    return (
                      <div key={i}
                        className={canBook && free ? "rag-free" : undefined}
                        onMouseDown={() => onCellDown(roomIdx, i)}
                        onMouseEnter={() => onCellEnter(roomIdx, i)}
                        style={{
                          gridColumn:i + 1, gridRow:1,
                          borderLeft: i % 2 === 0
                            ? `1px solid ${BORDER}`
                            : `1px dashed ${BORDER}66`,
                          background: mask.past[i] ? "#EFEAE4"
                                    : free         ? "#F0FDF4"
                                    : "transparent",
                          cursor: canBook && free ? "pointer" : "default",
                        }} />
                    );
                  })}

                  {(bookings[id] || []).map((b, bi) => (
                    <div key={bi}
                      title={`${b.title} (${b.time})${b.organizer ? " — " + b.organizer : ""}`}
                      style={{
                        gridColumn:`${b.startIdx + 1} / span ${b.span}`, gridRow:1,
                        margin:"4px 1px", borderRadius:5, background:"#3B82F6",
                        color:"#fff", fontSize:11, fontFamily:KH, padding:"0 6px",
                        overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis",
                        zIndex:2, lineHeight:`${GRID_ROW_H - 8}px`,
                      }}>
                      {b.title}
                    </div>
                  ))}

                  {hasSel && selRoomIdx === roomIdx && (
                    <div style={{
                      gridColumn:`${Math.max(0, selLo) + 1} / span ${Math.min(N_SLOTS - 1, selHi) - Math.max(0, selLo) + 1}`,
                      gridRow:1, margin:"4px 1px", borderRadius:5,
                      background:"#10B981", color:"#fff", fontSize:10, fontFamily:KH,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      gap:3, zIndex:3, pointerEvents:"none",
                      overflow:"hidden", whiteSpace:"nowrap",
                    }}>
                      <MdCheck size={12} style={{ flexShrink:0 }} />
                      {value.startTime}–{value.endTime}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && (
        <p style={{ fontSize:11, color:MUTED, fontFamily:KH, marginTop:4 }}>
          {t("កំពុងផ្ទុកកាលវិភាគ...")}
        </p>
      )}

      {hasSel && date && (
        <p style={{ marginTop:8, fontSize:12, color:BROWN, fontFamily:KH,
                    background:BG, border:`1px solid ${BORDER}`,
                    borderRadius:8, padding:"7px 12px", display:"inline-block" }}>
          {rooms[selRoomIdx]?.roomCode || `បន្ទប់ ${value.roomId}`} • {value.startTime}–{value.endTime} • {khDateShort(date)}
        </p>
      )}
    </div>
  );
}

function MeetingList({ defaultTab }) {
  const { t } = useLanguage();
  const [meetings,       setMeetings]       = useState([]);
  const [selected,       setSelected]       = useState(null);
  const [users,          setUsers]          = useState([]);
  const [peopleLoaded,   setPeopleLoaded]   = useState(false);
  const [rooms,          setRooms]          = useState([]);
  const [search,         setSearch]         = useState("");
  const [loading,        setLoading]        = useState(true);
  const [modal,          setModal]          = useState(false);
  const [form,           setForm]           = useState({});
  const [saving,         setSaving]         = useState(false);
  const [editId,         setEditId]         = useState(null);
  const [meetingStatuses,setMeetingStatuses]= useState([]);
  const [deleteId,       setDeleteId]       = useState(null);
  const [toast,          setToast]          = useState(null);
  const [err,            setErr]            = useState(null);
  const [page,           setPage]           = useState(0);
  const [pageSize,       setPageSize]       = useState(25);
  const [totalPages,     setTotalPages]     = useState(1);
  const [totalItems,     setTotalItems]     = useState(0);
  const [gridRefresh,    setGridRefresh]    = useState(0);

  const canBook = true;

  // The list endpoint returns a trimmed meeting; description/agenda/note only
  // come back from GET /meetings/{id}, so merge the full record in on select.
  const selectMeeting = (m) => {
    setSelected(m);
    const id = m?.meetingId ?? m?.id;
    if (!id) return;
    meetingAPI.getById(id)
      .then(res => {
        const full = res?.data?.data ?? res?.data;
        if (!full || typeof full !== "object") return;
        setSelected(prev =>
          (prev?.meetingId ?? prev?.id) === id ? { ...prev, ...full } : prev);
      })
      .catch(e => console.warn("meeting detail fetch failed:", e.message));
  };

  const load = async (p = page) => {
    setLoading(true);
    try {
      const res  = await meetingAPI.getAll(p, pageSize);
      const data = res?.data?.data ?? res?.data;
      const list = data?.content || [];
      setMeetings(list);
      setTotalPages(data?.totalPages || 1);
      setTotalItems(data?.totalElements || 0);
      if (!selected && list.length > 0) selectMeeting(list[0]);
      return list;
    } catch (e) {
      console.error(e);
      setErr(errMessage(e));
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, pageSize]);

  useEffect(() => {
    Promise.allSettled([
      userAPI.getAll(0, 200),
    ]).then(([usrRes]) => {
      if (usrRes.status === "fulfilled") {
        const d = usrRes.value?.data?.data ?? usrRes.value?.data;
        setUsers(d?.content || (Array.isArray(d) ? d : []));
      } else console.error("Users error:", usrRes.reason);

      setPeopleLoaded(true);
    });

    lookupAPI.getMeetingStatuses().then(r => {
      const d = r?.data?.data ?? r?.data;
      setMeetingStatuses(Array.isArray(d) ? d : []);
    }).catch(() => {});

    meetingRoomAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      const list = Array.isArray(d) ? d : [];
      const normalized = list.map(room => ({
        ...room,
        id:       room.id       ?? room.roomId,
        location: room.location ?? room.roomCode,
      }));
      setRooms(normalized);
    }).catch(e => console.error("Rooms error:", e));
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.roomId || !form.startTime || !form.endTime) {
      setErr(t("សូមជ្រើសរើសបន្ទប់ និងម៉ោង ដោយចុច ឬអូសលើតារាងបន្ទប់ទំនេរជាមុនសិន"));
      return;
    }
    setSaving(true);
    try {
      // Matches MeetingRequest exactly — the backend accepts no other fields.
      const payload = {
        title:       form.title       || "",
        description: form.description || "",
        meetingType: form.meetingType || undefined,
        meetingDate: form.meetingDate || undefined,
        startTime:   form.startTime ? (form.startTime.length === 5 ? form.startTime + ":00" : form.startTime) : undefined,
        endTime:     form.endTime   ? (form.endTime.length   === 5 ? form.endTime   + ":00" : form.endTime)   : undefined,
        roomId:      form.roomId      ? parseInt(form.roomId, 10)      : undefined,
        meetingLink: form.meetingLink || "",
        statusCode:  form.statusCode  || "SCHEDULED",
      };

      let savedId = editId;
      let created = null;
      if (editId) {
        await meetingAPI.update(editId, payload);
      } else {
        const res = await meetingAPI.create(payload);
        created = res?.data?.data ?? res?.data;
        savedId = created?.meetingId ?? created?.id;
      }

      const inviteIds = !editId ? (form.attendeeUserIds || []) : [];
      if (savedId && inviteIds.length > 0) {
        try {
          await meetingAPI.addAttendeesBulk(savedId, {
            userIds: inviteIds,
            role:    form.attendeeRole || "ATTENDEE",
          });
        } catch (inviteErr) {
          setErr("បង្កើតប្រជុំរួចហើយ ប៉ុន្តែការបន្ថែមអ្នករួមចំណែកបរាជ័យ: " + errMessage(inviteErr));
        }
      }

      if (!editId) setPage(0);
      const list = await load(0);

      let saved = list.find(m => (m.meetingId ?? m.id) === savedId);
      if (!editId && !saved && created) {
        saved = created;
        setMeetings(prev => [created, ...prev]);
      }
      if (saved) selectMeeting(saved);

      setToast(editId ? "កែប្រែប្រជុំបានជោគជ័យ!" : "បន្ថែមប្រជុំបានជោគជ័យ!");
      setModal(false); setForm({}); setEditId(null);
    } catch (e) {
      if (isConflictError(e)) {
        setErr(t("បន្ទប់នេះត្រូវបានកក់រួចហើយក្នុងម៉ោងនេះ"));
        setGridRefresh(k => k + 1);
      } else {
        setErr(errMessage(e));
      }
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (m) => {
    const id = m.meetingId ?? m.id;
    setEditId(id);
    setForm({
      title:       m.title       || "",
      description: m.description || "",
      meetingDate: m.meetingDate?.slice(0,10) || "",
      startTime:   m.startTime?.slice(0,5)   || "",
      endTime:     m.endTime?.slice(0,5)     || "",
      meetingType: m.meetingType || "",
      meetingLink: m.meetingLink || "",
      statusCode:  m.statusCode  || m.status  || "SCHEDULED",
      roomId:      m.roomId      ? String(m.roomId)      : "",
    });
    setModal(true);
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await meetingAPI.delete(id);
      const updated = meetings.filter(m => (m.meetingId ?? m.id) !== id);
      setMeetings(updated);
      setSelected(updated[0] || null);
      setToast(t("លុបប្រជុំបានជោគជ័យ!"));
    } catch (e) {
      setErr(isGenericServerError(e)
        ? "មិនអាចលុបកិច្ចប្រជុំនេះបានទេ។ សូមពិនិត្យថាតើកិច្ចប្រជុំមានអ្នករួមចំណែក ឬកំណត់ហេតុភ្ជាប់ ហើយលុបទិន្នន័យទាំងនោះជាមុនសិន។"
        : errMessage(e));
    }
  };

  const filtered = meetings.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [m.title, m.roomName, m.roomCode, m.organizerName]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  return (
    <div>
      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t("ស្វែងរកប្រជុំ....")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>
        <Can perm="meeting" action="create">
        <button onClick={() => { setEditId(null);
            setForm({ statusCode:"SCHEDULED", meetingDate: dateToISO(new Date()) });
            setModal(true); }}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, whiteSpace:"nowrap",
                   fontFamily:KH }}>
          {t("+ បន្ថែមប្រជុំ")}
        </button>
        </Can>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1.5fr", gap:16,
                    alignItems:"start" }}>

        <div>
          <div style={{ background:"var(--surface)", borderRadius:12,
                        border:`1px solid ${BORDER}`, overflow:"hidden" }}>
            <p style={{ padding:"12px 16px 8px", fontSize:12,
                        color:MUTED, fontFamily:KH,
                        borderBottom:`1px solid ${BORDER}` }}>
              កិច្ចប្រជុំ {new Date().toLocaleDateString("km-KH")}
            </p>

            {loading ? (
              <div style={{ padding:20 }}>
                {[1,2,3].map(i => (
                  <div key={i} style={{ height:72, background:"var(--border)",
                                         borderRadius:8, marginBottom:10 }} />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <p style={{ padding:"30px", textAlign:"center",
                          color:MUTED, fontSize:13, fontFamily:KH }}>
                {t("គ្មានប្រជុំ")}
              </p>
            ) : (
              filtered.map(m => {
                const sc = getStatusColor(m.statusCode || m.status);
                const sl = getStatusLabel(meetingStatuses, m.statusCode || m.status);
                const active = (selected?.meetingId ?? selected?.id) === (m.meetingId ?? m.id);
                return (
                  <div key={m.meetingId ?? m.id}
                    onClick={() => selectMeeting(m)}
                    style={{
                      padding:"14px 16px", cursor:"pointer",
                      background: active ? BG : "var(--surface)",
                      borderLeft: active
                        ? `3px solid ${BROWN}`
                        : "3px solid transparent",
                      borderBottom:`1px solid ${BORDER}`,
                      transition:"all 0.1s",
                    }}>
                    <div style={{ display:"flex", justifyContent:"space-between",
                                  marginBottom:5 }}>
                      <span style={{ fontSize:13, fontWeight:500,
                                     color:"var(--text)", fontFamily:KH }}>
                        {m.title || "ប្រជុំ"}
                      </span>
                      <Pill label={sl} color={sc} />
                    </div>
                    <p style={{ fontSize:11, color:MUTED, margin:"2px 0",
                                fontFamily:KH }}>
                      {m.meetingDate} {m.startTime?.slice(0,5)}
                    </p>
                    <p style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                      {roomLabel(m)}
                    </p>
                  </div>
                );
              })
            )}

            <Pagination
              page={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={n => { setPageSize(n); setPage(0); }}
            />
          </div>
        </div>

        {selected
          ? <MeetingDetail
              meeting={selected}
              onDeleted={handleDelete}
              onEdit={openEdit}
              users={users}
              peopleLoaded={peopleLoaded}
              meetingStatuses={meetingStatuses}
            />
          : (
            <div style={{ background:"var(--surface)", borderRadius:12,
                          border:`1px solid ${BORDER}`,
                          display:"flex", alignItems:"center",
                          justifyContent:"center", minHeight:200 }}>
              <p style={{ color:MUTED, fontSize:13, fontFamily:KH }}>
                {t("ជ្រើសរើសប្រជុំដើម្បីមើលព័ត៌មាន")}
              </p>
            </div>
          )
        }
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបប្រជុំនេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal && (
        <Modal
          width={780}
          title={editId ? "កែប្រែកិច្ចប្រជុំ" : "បន្ថែមកិច្ចប្រជុំថ្មី"}
          subtitle={t("បំពេញព័ត៌មានកិច្ចប្រជុំ")}
          onClose={() => { setModal(false); setForm({}); setEditId(null); }}
        >
          <FormField label={t("ចំណងជើងប្រជុំ")}
            value={form.title||""}
            onChange={v=>f("title",v)}
            placeholder={t("ចំណងជើង...")} />

          <FormField label="Agenda"
            value={form.description||""}
            onChange={v=>f("description",v)}
            rows={3}
            placeholder={t("ការពិពណ៌នាអំពីកិច្ចប្រជុំ...")} />

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr",
                        gap:"0 16px" }}>
            <FormField label={t("ថ្ងៃប្រជុំ")}
              type="date"
              value={form.meetingDate||""}
              onChange={v=>f("meetingDate",v)} />
            <FormField label={t("ប្រភេទប្រជុំ")}
              value={form.meetingType||""}
              onChange={v=>f("meetingType",v)}
              options={["INTERNAL","EXTERNAL","ONLINE"]} />
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED,
                            marginBottom:6, fontFamily:KH }}>
              {t("បន្ទប់ប្រជុំ (ចុច ឬអូសលើម៉ោងទំនេរ ដើម្បីជ្រើសរើសបន្ទប់ និងម៉ោង)")}
            </label>
            {form.meetingDate ? (
              <RoomAvailabilityGrid
                rooms={rooms}
                date={form.meetingDate}
                value={{ roomId: form.roomId, startTime: form.startTime,
                         endTime: form.endTime }}
                onSelect={(roomId, startTime, endTime) =>
                  setForm(p => ({ ...p, roomId, startTime, endTime }))}
                onDateChange={(iso) => f("meetingDate", iso)}
                excludeMeetingId={editId}
                canBook={canBook}
                refreshKey={gridRefresh}
              />
            ) : (
              <p style={{ fontSize:12, color:MUTED, fontFamily:KH,
                          background:BG, border:`1px dashed ${BORDER}`,
                          borderRadius:8, padding:"12px 14px" }}>
                {t("សូមជ្រើសរើសថ្ងៃប្រជុំជាមុន ដើម្បីមើលបន្ទប់ទំនេរ")}
              </p>
            )}
          </div>

          {editId && (
            <FormField label={t("ស្ថានភាព")}
              value={form.statusCode||"SCHEDULED"}
              onChange={v=>f("statusCode",v)}
              options={meetingStatuses.length
                ? [...meetingStatuses]
                    .filter(s => s.isActive !== false)
                    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                    .map(s => ({ value: s.statusCode, label: s.labelKh || s.labelEn || s.statusCode }))
                : ["DRAFT","SCHEDULED","CONFIRMED","IN_PROGRESS","COMPLETED","CANCELLED","POSTPONED","RESCHEDULED"]} />
          )}

          <FormField label={t("តំណភ្ជាប់")}
            value={form.meetingLink||""}
            onChange={v=>f("meetingLink",v)}
            placeholder="https://meet.google.com/..." />

          {!editId ? (
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:6, fontFamily:KH, fontWeight:600 }}>
                {t("អ្នករួមចំណែក (ជ្រើសរើសមន្ត្រីដើម្បីអញ្ជើញ)")}
              </label>
              <UserPicker
                users={users}
                peopleLoaded={peopleLoaded}
                pickedIds={form.attendeeUserIds || []}
                onToggle={(uid) => setForm(p => {
                  const ids = p.attendeeUserIds || [];
                  return { ...p, attendeeUserIds: ids.includes(uid)
                    ? ids.filter(x => x !== uid) : [...ids, uid] };
                })}
              />
              {(form.attendeeUserIds || []).length > 0 && (
                <>
                  <label style={{ display:"block", fontSize:12, color:MUTED,
                                  marginBottom:4, fontFamily:KH }}>
                    {t("តួនាទី (សម្រាប់អ្នកជ្រើសរើសទាំងអស់)")}
                  </label>
                  <select value={form.attendeeRole||"ATTENDEE"}
                    onChange={e => f("attendeeRole", e.target.value)}
                    style={{ width:"100%", padding:"9px 12px",
                             border:`1px solid ${BORDER}`, borderRadius:8,
                             fontSize:13, outline:"none", background:"var(--surface)",
                             fontFamily:KH, boxSizing:"border-box" }}>
                    {ATTENDEE_ROLES.map(r => (
                      <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
          ) : (
            <p style={{ fontSize:11, color:MUTED, fontFamily:KH, marginBottom:14 }}>
              {t("អ្នករួមចំណែកអាចគ្រប់គ្រងបាននៅក្នុងផ្ទាំង «អ្នករួមចំណែក» របស់កិច្ចប្រជុំ")}
            </p>
          )}

          <div style={{ display:"flex", justifyContent:"flex-end",
                        gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(false); setForm({}); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave}
              style={{ opacity: saving ? 0.7 : 1 }}>
              {saving ? "កំពុង..." : "រក្សាទុករបស់កិច្ចប្រជុំ"}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function MeetingRooms() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const canRoomAction = ["view", "edit", "delete"].some(a => canDo(user, "meeting-room", a));
  const [list,       setList]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(false);
  const [form,       setForm]       = useState({});
  const [saving,     setSaving]     = useState(false);
  const [page,       setPage]       = useState(0);
  const [pageSize,   setPageSize]   = useState(25);
  const [viewRoom,   setViewRoom]   = useState(null);
  const [deleteId,   setDeleteId]   = useState(null);
  const [toast,      setToast]      = useState(null);
  const [err,        setErr]        = useState(null);
  const [imageFile,  setImageFile]  = useState(null);
  const [lightbox,   setLightbox]   = useState(null);
  const [meetings,   setMeetings]   = useState([]);
  const [, setTick]  = useState(0);

  const ROOM_STATUS = { AVAILABLE:"ទំនេរ", IN_USE:"កំពុងប្រើប្រាស់", MAINTENANCE:"កំពុងជួសជុល", CLOSED:"បិទ" };
  const ROOM_STATUS_COLOR = { AVAILABLE:"#3B82F6", IN_USE:"#F59E0B", MAINTENANCE:"#8B6B50", CLOSED:"#EF4444" };

  const liveStatusFor = (room) => {
    const rid = room.roomId ?? room.id;
    const live = meetings.find(m =>
      String(m.roomId) === String(rid) && isMeetingLiveNow(m));
    if (live) {
      return { label:"កំពុងប្រជុំ", color:getStatusColor("IN_PROGRESS"), meeting:live };
    }
    return {
      label:  ROOM_STATUS[room.status] || room.status || "—",
      color:  ROOM_STATUS_COLOR[room.status] || "#94A3B8",
      meeting:null,
    };
  };

  const load = async () => {
    setLoading(true);
    try {
      const res  = await meetingRoomAPI.getAll();
      const data = res?.data?.data ?? res?.data;
      setList(Array.isArray(data) ? data : data?.content || []);
      setPage(0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }

    try {
      const mr = await meetingAPI.getAll(0, 200);
      const md = mr?.data?.data ?? mr?.data;
      setMeetings(md?.content || (Array.isArray(md) ? md : []));
    } catch (e) { console.warn("Meetings load for room status failed:", e?.message); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        roomCode:   form.roomCode   || "",
        location:   form.location   || "",
        capacity:   parseInt(form.capacity) || 0,
        status:     form.status     || "AVAILABLE",
        facilities: form.facilities || "",
      };
      let roomId = form._id;
      if (form._id) {
        await meetingRoomAPI.update(form._id, payload);
      } else {
        const createRes = await meetingRoomAPI.create(payload);
        const created = createRes?.data?.data ?? createRes?.data;
        roomId = created?.roomId ?? created?.id;
      }

      if (imageFile && roomId) {
        try {
          await meetingRoomAPI.uploadImage(roomId, imageFile);
        } catch (uploadErr) {
          console.warn("Image upload failed:", uploadErr.message);
          setErr("រក្សាទុកបន្ទប់រួចហើយ ប៉ុន្តែការបញ្ចូលរូបភាពបរាជ័យ: " + errMessage(uploadErr));
        }
      }

      await load();
      setToast(form._id ? "កែប្រែបន្ទប់ប្រជុំបានជោគជ័យ!" : "បន្ថែមបន្ទប់ប្រជុំបានជោគជ័យ!");
      setModal(false); setForm({}); setImageFile(null);
    } catch (e) {
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await meetingRoomAPI.delete(id);
      if (viewRoom && (viewRoom.roomId ?? viewRoom.id) === id) setViewRoom(null);
      await load();
      setToast(t("លុបបន្ទប់ប្រជុំបានជោគជ័យ!"));
    } catch (e) {
      let inUse = isRoomInUseError(e);
      if (!inUse) {
        try {
          const r = await meetingAPI.getAll(0, 1, undefined, id);
          const d = r?.data?.data ?? r?.data;
          const list = d?.content || (Array.isArray(d) ? d : []);
          inUse = (d?.totalElements ?? list.length) > 0;
        } catch {}
      }
      setErr(inUse
        ? "មិនអាចលុបបន្ទប់នេះបានទេ ព្រោះមានកិច្ចប្រជុំកំពុងប្រើប្រាស់បន្ទប់នេះ។ សូមលុប ឬផ្លាស់ប្តូរបន្ទប់របស់កិច្ចប្រជុំទាំងនោះជាមុនសិន។"
        : errMessage(e));
    }
  };

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)",
                    gap:12, marginBottom:20 }}>
        {[
          { label:"បន្ទប់សរុប",         value: list.length },
          { label:"បន្ទប់ទំនេរ",      value: list.filter(r=>r.status==="AVAILABLE").length },
          { label:"បន្ទប់មិនទំនេរ",  value: list.filter(r=>r.status!=="AVAILABLE").length },
          { label:"កំពុងប្រជុំ",       value: list.filter(r=>liveStatusFor(r).meeting).length },
        ].map((s,i) => (
          <div key={i} style={{ background:"var(--surface)", borderRadius:10,
                                 padding:16, border:`1px solid ${BORDER}` }}>
            <p style={{ margin:"0 0 6px", fontSize:12,
                        color:MUTED, fontFamily:KH }}>{s.label}</p>
            <p style={{ margin:0, fontSize:28, fontWeight:700,
                        color:BROWN, fontFamily:MO }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", justifyContent:"space-between",
                    marginBottom:16 }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px", marginRight:12 }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input placeholder={t("ស្វែងរកបន្ទប់....")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>
        <Can perm="meeting-room" action="create">
        <button onClick={() => { setForm({}); setImageFile(null); setModal(true); }}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, whiteSpace:"nowrap",
                   fontFamily:KH }}>
          {t("+ បន្ថែមបន្ទប់")}
        </button>
        </Can>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr style={{ background:BG }}>
              {["កូដ","ទីតាំង","ចំនួន","ស្ថានភាព",
                ...(canRoomAction ? ["សកម្មភាព"] : [])].map(h => (
                <th key={h} style={{ padding:"12px 16px", textAlign:"left",
                                      color:MUTED, fontWeight:500,
                                      borderBottom:`1px solid ${BORDER}`,
                                      fontSize:12, fontFamily:KH,
                                      background:BG, position:"sticky", top:0, zIndex:1 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3].map(i => (
                <tr key={i}>
                  {[1,2,3,4,...(canRoomAction ? [5] : [])].map(j => (
                    <td key={j} style={{ padding:"12px 16px",
                                          borderBottom:`1px solid ${BORDER}` }}>
                      <div style={{ height:14, background:"var(--border)",
                                     borderRadius:4 }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : list.slice(page * pageSize, page * pageSize + pageSize).map(r => (
              <tr key={r.roomId ?? r.id}
                onMouseEnter={e => e.currentTarget.style.background=BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <td style={{ padding:"12px 16px", fontSize:13,
                              fontFamily:KH }}>{r.roomCode||"—"}</td>
                <td style={{ padding:"12px 16px", fontSize:13,
                              fontFamily:KH }}>{r.location||"—"}</td>
                <td style={{ padding:"12px 16px", fontSize:13,
                              fontFamily:KH }}>{r.capacity}</td>
                <td style={{ padding:"12px 16px" }}>
                  {(() => {
                    const ls = liveStatusFor(r);
                    return (
                      <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                        <Pill label={ls.label} color={ls.color} />
                        {ls.meeting && (
                          <span style={{ fontSize:10, color:MUTED, fontFamily:KH }}>
                            {ls.meeting.title || "ប្រជុំ"} · {(ls.meeting.startTime||"").slice(0,5)}–{(ls.meeting.endTime||"").slice(0,5)}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                {canRoomAction && (
                <td style={{ padding:"12px 16px",
                              borderBottom:`1px solid ${BORDER}` }}>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <Can perm="meeting-room" action="view">
                    <button onClick={() => {
                        setViewRoom(r);
                        if (r.imageUrl) {
                          meetingRoomAPI.getImageUrl(r.roomId ?? r.id)
                            .then(res => {
                              const url = res?.data?.downloadUrl ?? res?.data?.imageUrl ?? res?.data?.url ?? res?.data;
                              if (typeof url === "string")
                                setViewRoom(prev => prev ? { ...prev, imageSrc: url } : prev);
                            })
                            .catch(() => {});
                        }
                      }}
                      style={{ background:"none", border:"none",
                               cursor:"pointer", color:"#3B82F6",
                               display:"flex", alignItems:"center", padding:"4px" }}>
                      <MdVisibility size={17}/>
                    </button>
                    </Can>
                    <Can perm="meeting-room" action="edit">
                    <button onClick={() => {
                      const rId = r.roomId ?? r.id;
                      setForm({ _id:rId, roomCode:r.roomCode,
                                location:r.location, capacity:r.capacity,
                                status:r.status, facilities:r.facilities,
                                imageSrc:null });
                      setImageFile(null);
                      setModal(true);
                      if (r.imageUrl) {
                        meetingRoomAPI.getImageUrl(rId)
                          .then(res => {
                            const url = res?.data?.downloadUrl ?? res?.data?.imageUrl ?? res?.data?.url ?? res?.data;
                            if (typeof url === "string")
                              setForm(prev => ({ ...prev, imageSrc:url }));
                          })
                          .catch(() => {});
                      }
                    }} style={{ background:"none", border:"none",
                                cursor:"pointer", color:"var(--muted)",
                                display:"flex", alignItems:"center", padding:"4px" }}>
                      <MdEdit size={17}/>
                    </button>
                    </Can>
                    <Can perm="meeting-room" action="delete">
                    <button onClick={() => handleDelete(r.roomId ?? r.id)}
                      style={{ background:"none", border:"none",
                               cursor:"pointer", color:"#EF4444",
                               display:"flex", alignItems:"center", padding:"4px" }}>
                      <MdDelete size={17}/>
                    </button>
                    </Can>
                  </div>
                </td>
                )}
              </tr>
            ))}
          </tbody>
        </TableScroll>
        <Pagination
          page={page}
          totalPages={Math.max(1, Math.ceil(list.length / pageSize))}
          totalItems={list.length}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបបន្ទប់ប្រជុំនេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {viewRoom && (
        <Modal
          title={viewRoom.roomCode || "បន្ទប់ប្រជុំ"}
          subtitle={(() => { const ls = liveStatusFor(viewRoom); return <Pill label={ls.label} color={ls.color} />; })()}
          onClose={() => setViewRoom(null)}
        >
          {viewRoom.imageSrc && (
            <div style={{ position:"relative", marginBottom:16 }}>
              <img src={viewRoom.imageSrc} alt={viewRoom.roomCode || "room"}
                onClick={() => setLightbox(viewRoom.imageSrc)}
                title={t("ចុចដើម្បីមើលរូបពេញ")}
                style={{ width:"100%", maxHeight:260, objectFit:"contain",
                         background:BG, borderRadius:8, cursor:"zoom-in",
                         border:`1px solid ${BORDER}` }} />
              <span style={{ position:"absolute", right:8, bottom:8,
                             background:"rgba(0,0,0,0.55)", color:"#fff",
                             fontSize:11, padding:"3px 8px", borderRadius:6,
                             fontFamily:KH, pointerEvents:"none" }}>
                {t("ចុចដើម្បីមើលរូបពេញ")}
              </span>
            </div>
          )}
          {[
            { icon:<MdBadge size={16}/>,        label:"លេខសម្គាល់",   value: viewRoom.roomId ?? viewRoom.id },
            { icon:<MdAssignment size={16}/>,    label:"កូដបន្ទប់",      value: viewRoom.roomCode },
            { icon:<MdLocationOn size={16}/>,    label:"ទីតាំងបន្ទប់",   value: viewRoom.location },
            { icon:<MdPeople size={16}/>,        label:"អ្នកចូលរួមអតិបរមា",          value: viewRoom.capacity ? `${viewRoom.capacity} នាក់` : "—" },
            { icon:<MdCheckCircle size={16}/>,   label:"ស្ថានភាព",       value: liveStatusFor(viewRoom).label },
            { icon:<MdDescription size={16}/>,   label:"គ្រឿងបរិក្ខារ",  value: viewRoom.facilities },
            { icon:<MdCalendarToday size={16}/>, label:"បង្កើតនៅ",       value: viewRoom.createdAt
                ? new Date(viewRoom.createdAt).toLocaleDateString("en-GB") : "—" },
            { icon:<MdEdit size={16}/>,          label:"កែប្រែនៅ",       value: viewRoom.updatedAt
                ? new Date(viewRoom.updatedAt).toLocaleDateString("en-GB") : "—" },
          ].map((r,i) => (
            <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10,
                                   background:BG, padding:"9px 14px",
                                   borderRadius:8, marginBottom:6 }}>
              <span style={{ flexShrink:0, marginTop:2, color:MUTED, display:"flex", alignItems:"center" }}>{r.icon}</span>
              <div>
                <p style={{ fontSize:11, color:MUTED, marginBottom:2,
                             fontFamily:KH }}>{r.label}</p>
                <p style={{ fontSize:13, fontWeight:500, color:"var(--text)",
                             fontFamily:KH }}>{r.value || "—"}</p>
              </div>
            </div>
          ))}

          <div style={{ marginTop:20, display:"flex",
                        justifyContent:"space-between" }}>
            <Can perm="meeting-room" action="edit">
            <Btn variant="outline" onClick={() => {
              setForm({ _id:viewRoom.roomId ?? viewRoom.id,
                        roomCode:viewRoom.roomCode, location:viewRoom.location,
                        capacity:viewRoom.capacity, status:viewRoom.status,
                        facilities:viewRoom.facilities,
                        imageSrc:viewRoom.imageSrc || null });
              setImageFile(null);
              setViewRoom(null);
              setModal(true);
            }}
            style={{display:"flex",alignItems:"center",gap:5}}>
              <MdEdit size={14}/> {t("កែប្រែ")}
            </Btn>
            </Can>
            <Can perm="meeting-room" action="delete">
            <Btn variant="danger"
              onClick={() => handleDelete(viewRoom.roomId ?? viewRoom.id)}
              style={{display:"flex",alignItems:"center",gap:5}}>
              <MdDelete size={14}/> {t("លុប")}
            </Btn>
            </Can>
          </div>
        </Modal>
      )}

      {modal && (
        <Modal
          title={form._id ? "កែប្រែបន្ទប់ប្រជុំ" : "បន្ថែមបន្ទប់ប្រជុំថ្មី"}
          onClose={() => { setModal(false); setForm({}); setImageFile(null); }}
        >
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr",
                        gap:"0 16px" }}>
            <FormField label={t("កូដបន្ទប់")}
              value={form.roomCode||""}
              onChange={v=>f("roomCode",v)}
              placeholder="A101..." />
            <FormField label={t("អ្នកចូលរួមអតិបរមា")}
              type="number"
              value={form.capacity||""}
              onChange={v=>f("capacity",v)} />
          </div>
          <FormField label={t("ទីតាំងបន្ទប់")}
            value={form.location||""}
            onChange={v=>f("location",v)}
            placeholder={t("ជាន់ ១, អគារ A...")} />
          {form._id && (
            <FormField label={t("ស្ថានភាព")}
              value={form.status||"AVAILABLE"}
              onChange={v=>f("status",v)}
              options={Object.entries(ROOM_STATUS).map(([value, label]) => ({ value, label }))} />
          )}
          <FormField label={t("គ្រឿងបរិក្ខារ")}
            value={form.facilities||""}
            onChange={v=>f("facilities",v)}
            rows={2}
            placeholder="Projector, WiFi, AC..." />

          <div style={{ marginBottom:14 }}>
            <label style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, color:MUTED,
                            marginBottom:6, fontFamily:KH, fontWeight:600 }}>
              <MdImage size={15}/> {t("រូបភាពបន្ទប់")}
            </label>
            {!imageFile && form.imageSrc && (
              <img src={form.imageSrc} alt="current"
                style={{ width:"100%", height:150, objectFit:"cover",
                         borderRadius:8, marginBottom:8,
                         border:`1px solid ${BORDER}` }} />
            )}
            <label style={{
              display:"flex", alignItems:"center", gap:12,
              padding:"14px 16px", border:`2px dashed ${BORDER}`,
              borderRadius:8, background: imageFile ? "#F0FDF4" : BG,
              cursor:"pointer", transition:"all 0.2s",
            }}>
              <span style={{ fontSize:28, display:"flex", alignItems:"center", color:MUTED }}>
                {imageFile ? <MdCheckCircle size={28} color="#10B981"/> : <MdFolder size={28}/>}
              </span>
              <div style={{ flex:1 }}>
                {imageFile ? (
                  <div>
                    <p style={{ fontSize:13, fontWeight:600, color:"#065F46",
                                fontFamily:KH, marginBottom:2 }}>
                      {imageFile.name}
                    </p>
                    <p style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                      {(imageFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p style={{ fontSize:13, color:BROWN, fontFamily:KH,
                                marginBottom:2 }}>
                      {t("ចុចដើម្បីជ្រើសរើសរូបភាព")}
                    </p>
                    <p style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                      JPG, PNG, WEBP
                    </p>
                  </div>
                )}
              </div>
              {imageFile && (
                <button onClick={e => { e.preventDefault(); setImageFile(null); }}
                  style={{ background:"#FEE2E2", border:"none", borderRadius:6,
                           padding:"4px 8px", cursor:"pointer",
                           color:"#991B1B", fontSize:12 }}>
                  {t("លុប")}
                </button>
              )}
              <input type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={e => setImageFile(e.target.files[0] || null)}
                style={{ display:"none" }} />
            </label>
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end",
                        gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(false); setForm({}); setImageFile(null); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave}
              style={{ opacity: saving ? 0.7 : 1 }}>
              {saving ? "កំពុង..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position:"fixed", inset:0, zIndex:9999,
                   background:"rgba(0,0,0,0.85)", display:"flex",
                   alignItems:"center", justifyContent:"center",
                   padding:24, cursor:"zoom-out" }}>
          <button onClick={() => setLightbox(null)}
            style={{ position:"absolute", top:18, right:22, background:"none",
                     border:"none", color:"#fff", fontSize:34, cursor:"pointer",
                     lineHeight:1 }}>×</button>
          <img src={lightbox} alt="room full"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth:"95%", maxHeight:"95%", objectFit:"contain",
                     borderRadius:8, boxShadow:"0 8px 40px rgba(0,0,0,0.5)" }} />
        </div>
      )}
    </div>
  );
}

export default function Activities({ defaultTab = "list" }) {
  return defaultTab === "rooms"
    ? <MeetingRooms />
    : <MeetingList />;
}